import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { Button } from "../ui/button";
import { Info, Loader2, AlertTriangle, Sparkles, RefreshCw } from "lucide-react";

/**
 * Hover-trigger info icon that opens a small card showing the AI-generated
 * attachment summary. Handles all 4 summary states: missing, generating,
 * ready, failed.
 */
export default function FileSummaryCard({ file, onRefreshed }) {
  const [busy, setBusy] = useState(false);

  const status = file?.summary_status; // generating | ready | failed | undefined
  const text = file?.summary;
  const model = file?.summary_model;

  const triggerRegenerate = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post(`/files/${file.id}/resummarize`);
      toast.message("Summarising attachment…");
      onRefreshed?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start summary");
    } finally {
      setBusy(false);
    }
  };

  const iconColor =
    status === "ready"
      ? "text-primary"
      : status === "failed"
      ? "text-destructive"
      : status === "generating"
      ? "text-amber-500"
      : "text-muted-foreground";

  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={`p-0.5 rounded-sm hover:bg-secondary transition ${iconColor}`}
          data-testid={`file-summary-trigger-${file.id}`}
          aria-label="Attachment summary"
          onClick={(e) => e.stopPropagation()}
        >
          {status === "generating"
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : status === "failed"
            ? <AlertTriangle className="h-3.5 w-3.5" />
            : status === "ready"
            ? <Sparkles className="h-3.5 w-3.5" />
            : <Info className="h-3.5 w-3.5" />}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="start"
        className="w-80 p-0 border-border rounded-sm shadow-lg"
        data-testid={`file-summary-card-${file.id}`}
      >
        <div className="px-3 py-2 border-b border-border bg-secondary/40">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">[ attachment summary ]</div>
          <div className="font-semibold text-sm truncate mt-0.5" title={file.name}>{file.name}</div>
        </div>

        <div className="p-3 text-sm">
          {!status && file.status !== "ready" && (
            <div className="text-muted-foreground italic">
              Summary will appear after Tensornex finishes processing this file.
            </div>
          )}

          {status === "generating" && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Generating summary…</span>
            </div>
          )}

          {status === "ready" && text && (
            <p className="leading-relaxed text-foreground whitespace-pre-wrap">{text}</p>
          )}

          {status === "ready" && !text && (
            <div className="text-muted-foreground italic">Summary is empty.</div>
          )}

          {status === "failed" && (
            <div>
              <div className="flex items-start gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="text-sm">{file.summary_error || "Summary failed."}</span>
              </div>
            </div>
          )}
        </div>

        {file.status === "ready" && (
          <div className="px-3 py-2 border-t border-border bg-secondary/30 flex items-center justify-between">
            <span className="text-[10px] font-mono text-muted-foreground truncate">
              {model ? `via ${model}` : "Default model"}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 rounded-sm text-[11px]"
              onClick={triggerRegenerate}
              disabled={busy || status === "generating"}
              data-testid={`file-summary-regen-${file.id}`}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} />
              {status === "failed" ? "Retry" : "Regenerate"}
            </Button>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
